import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import ThemePreview from "../components/ThemePreview";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { THEME_PRESETS, PRESET_ORDER, getPreset } from "../lib/themes";
import { Check, Smartphone, Eye, Palette, Sparkles, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const COPY = {
  title: { de: "Theme auswählen", en: "Choose a theme", tr: "Tema seç" },
  subtitle: {
    de: "Ein Klick — komplett neues Design. Live-Vorschau auf der rechten Seite. Du kannst jederzeit wechseln.",
    en: "One click — completely new design. Live preview on the right. You can switch anytime.",
    tr: "Tek tıkla tamamen yeni tasarım. Sağ tarafta canlı önizleme. İstediğin zaman değiştirebilirsin.",
  },
  applied: { de: "Aktiv", en: "Active", tr: "Aktif" },
  apply: { de: "Anwenden", en: "Apply", tr: "Uygula" },
  preview: { de: "Vorschau", en: "Preview", tr: "Önizleme" },
  openMenu: { de: "Menü öffnen", en: "Open menu", tr: "Menüyü aç" },
  saving: { de: "Speichern…", en: "Saving…", tr: "Kaydediliyor…" },
  saved: { de: "Theme aktiviert ✨", en: "Theme activated ✨", tr: "Tema etkinleştirildi ✨" },
  livePreviewBadge: { de: "LIVE-VORSCHAU · MOBIL", en: "LIVE PREVIEW · MOBILE", tr: "CANLI ÖNİZLEME · MOBİL" },
  details: { de: "Was du bekommst", en: "What you get", tr: "Ne alıyorsun" },
};

const FEATURE_LABELS = {
  layout: { de: "Layout", en: "Layout", tr: "Düzen" },
  buttons: { de: "Buttons", en: "Buttons", tr: "Butonlar" },
  typography: { de: "Typografie", en: "Typography", tr: "Tipografi" },
  cards: { de: "Menü-Karten", en: "Menu cards", tr: "Menü kartları" },
};

export default function ThemePicker() {
  const { id } = useParams();
  const { lang, t } = useLang();
  const tx = (k) => COPY[k]?.[lang] || COPY[k]?.de || k;
  const tCust = t("dashboard.themeCustomizer");

  const [restaurant, setRestaurant] = useState(null);
  const [activeId, setActiveId] = useState("default");
  const [previewId, setPreviewId] = useState("default");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/restaurants/${id}`);
        setRestaurant(data);
        const pr = data?.theme?.preset || "default";
        setActiveId(pr);
        setPreviewId(pr);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [id]);

  const preview = getPreset(previewId);

  const apply = async (presetId) => {
    const p = getPreset(presetId);
    setSaving(true);
    try {
      const themePayload = {
        ...(restaurant?.theme || {}),
        preset: p.id,
        primary_color: p.primary,
        accent_color: p.accent,
        background_color: p.bg,
        text_color: p.text,
        font_family: p.fontHeading.split(",")[0].replace(/['"]/g, "").trim(),
        layout: p.layout,
        button_style: p.btnStyle === "outline" ? "rounded" : p.btnRadius === "0" ? "sharp" : "pill",
        dark_mode: p.id !== "default" && p.id !== "minimal_cafe",
      };
      await api.put(`/restaurants/${id}/theme`, themePayload);
      setActiveId(p.id);
      toast.success(tx("saved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const featureChips = (p) => [
    { k: "layout", v: { de: { grid: "Raster", list: "Liste", cards: "Karten" }, en: { grid: "Grid", list: "List", cards: "Cards" }, tr: { grid: "Izgara", list: "Liste", cards: "Kartlar" } }[lang]?.[p.layout] || p.layout },
    { k: "buttons", v: { solid: "Solid", outline: "Outline", block: "Block" }[p.btnStyle] || p.btnStyle },
    { k: "typography", v: p.fontHeading.split(",")[0].replace(/['"]/g, "").trim() },
    { k: "cards", v: { soft: "Soft", bordered: "Bordered", floating: "Floating", flat: "Flat" }[p.cardStyle] || p.cardStyle },
  ];

  return (
    <DashboardLayout restaurantId={id}>
      <div className="space-y-6" data-testid="theme-picker-page">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-2">
              <Palette className="h-3 w-3" /> Theme · Live Preview
            </div>
            <h1 className="text-3xl font-bold tracking-tight mt-1">{tx("title")}</h1>
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed max-w-2xl">{tx("subtitle")}</p>
          </div>
          <Link
            to={`/dashboard/restaurants/${id}/theme`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary hover:bg-secondary/80 text-sm font-semibold transition-colors"
            data-testid="link-theme-customizer"
          >
            <Palette className="h-4 w-4" />
            {tCust}
          </Link>
        </div>

        <div className="grid lg:grid-cols-[1fr_360px] gap-6 items-start">
          {/* THEME GRID */}
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {PRESET_ORDER.map((pid) => {
              const p = THEME_PRESETS[pid];
              const isActive = activeId === pid;
              const isPreview = previewId === pid;
              return (
                <div
                  key={pid}
                  onMouseEnter={() => setPreviewId(pid)}
                  onFocus={() => setPreviewId(pid)}
                  onClick={() => setPreviewId(pid)}
                  className={`group relative rounded-3xl border bg-card cursor-pointer transition-all duration-300 ${
                    isPreview
                      ? "border-purple-500 shadow-2xl shadow-purple-500/20 -translate-y-1"
                      : "border-zinc-200 dark:border-zinc-800 hover:border-purple-500/50"
                  }`}
                  data-testid={`theme-card-${pid}`}
                  tabIndex={0}
                >
                  {/* Thumb */}
                  <div className="p-3 pb-0">
                    <div className="overflow-hidden rounded-2xl" style={{ height: 220 }}>
                      <div className="origin-top-left" style={{ transform: "scale(0.42)", width: "238%", height: "238%" }}>
                        <ThemePreview preset={p} restaurantName={restaurant?.name || "Mein Restaurant"} compact />
                      </div>
                    </div>
                  </div>

                  {/* Active badge */}
                  {isActive && (
                    <div className="absolute top-5 right-5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-widest shadow-lg shadow-emerald-500/40">
                      <Check className="h-3 w-3" /> {tx("applied")}
                    </div>
                  )}

                  {/* Meta */}
                  <div className="p-5 pt-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="flex gap-1">
                        {p.swatches.map((c, i) => (
                          <span key={i} className="w-3.5 h-3.5 rounded-full border border-zinc-200 dark:border-zinc-700" style={{ background: c }} />
                        ))}
                      </div>
                    </div>
                    <h3 className="text-lg font-bold tracking-tight">{p.name[lang] || p.name.de}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{p.tagline[lang] || p.tagline.de}</p>

                    <Button
                      onClick={(e) => { e.stopPropagation(); apply(pid); }}
                      disabled={saving || isActive}
                      size="sm"
                      className={`mt-4 w-full rounded-full font-bold transition-all ${
                        isActive
                          ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10 cursor-default"
                          : "bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-500 hover:to-fuchsia-500 text-white shadow-md shadow-purple-600/30"
                      }`}
                      data-testid={`theme-apply-${pid}`}
                    >
                      {isActive ? (
                        <><Check className="h-3.5 w-3.5 mr-1.5" /> {tx("applied")}</>
                      ) : saving ? (
                        tx("saving")
                      ) : (
                        <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> {tx("apply")}</>
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* LIVE PREVIEW SIDEBAR */}
          <aside className="lg:sticky lg:top-6">
            <div className="rounded-3xl border border-zinc-200 dark:border-zinc-800 bg-card p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-purple-600 dark:text-purple-400">
                  <Smartphone className="h-3 w-3" /> {tx("livePreviewBadge")}
                </div>
                {restaurant?.slug && (
                  <Link
                    to={`/m/${restaurant.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-zinc-500 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
                    data-testid="theme-open-public-menu"
                  >
                    {tx("openMenu")} <ExternalLink className="h-3 w-3" />
                  </Link>
                )}
              </div>

              {/* Phone frame */}
              <div className="mx-auto rounded-[2rem] border-[6px] border-zinc-900 dark:border-zinc-700 bg-zinc-900 shadow-2xl shadow-purple-500/15" style={{ width: 268 }}>
                <div className="overflow-hidden rounded-[1.5rem]">
                  <ThemePreview preset={preview} restaurantName={restaurant?.name || "Mein Restaurant"} />
                </div>
              </div>

              {/* Theme details */}
              <div className="mt-5">
                <h3 className="font-bold text-lg tracking-tight" style={{ fontFamily: preview.fontHeading }}>
                  {preview.name[lang] || preview.name.de}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">{preview.tagline[lang] || preview.tagline.de}</p>

                <div className="mt-4 text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-2">{tx("details")}</div>
                <div className="grid grid-cols-2 gap-2">
                  {featureChips(preview).map((c, i) => (
                    <div key={i} className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-background px-2.5 py-1.5">
                      <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">{FEATURE_LABELS[c.k][lang] || c.k}</div>
                      <div className="text-xs font-bold mt-0.5 truncate">{c.v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </DashboardLayout>
  );
}
